import { type AnnotationFeedbackDetail, useCurrentUser } from 'api';
import { formatDate } from 'date';
import { useTranslation } from 'i18n';
import { useState } from 'react';
import {
  Avatar,
  Box,
  ClickAwayListener,
  ConfirmationDialog,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Stack,
  Typography,
} from 'ui';
import { DeleteOutlined, Edit, MoreHorizOutlined } from 'ui/icons';

import { getNameInitials } from '../../../name';
import { formatAiFeedbackType } from '../../utils/formatAiFeedbackType/formatAiFeedbackType';
import { COMMENT_SIZE } from '../CommentMarker/CommentMarker';

export const CommentBubble = ({
  comment,
  anchorEl,
  open,
  onClose,
  onEdit,
  onDelete,
}: {
  comment: AnnotationFeedbackDetail;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const { t } = useTranslation('features');
  const { data: currentUser } = useCurrentUser();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { author, created_at, detail, feedback_type } = comment;

  const initials = getNameInitials(author.full_name);
  const formattedDate = formatDate(created_at);
  const formattedComment = `${formatAiFeedbackType(feedback_type)}${detail ? `: ${detail}` : ''}`;

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    setIsDeleteDialogOpen(false);
    onDelete();
  };

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      disablePortal
      placement="right-start"
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, -COMMENT_SIZE.height],
          },
        },
        {
          enabled: true,
          name: 'preventOverflow',
          options: {
            altAxis: true,
            altBoundary: true,
            padding: 8,
            rootBoundary: 'document',
            tether: true,
          },
        },
      ]}
      sx={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <div>
          <Paper
            elevation={3}
            onClick={onClose}
            sx={{
              borderRadius: '10px',
              cursor: 'pointer',
              minWidth: 250,
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              sx={{
                px: 1,
                py: 1.5,
                alignItems: 'center',
              }}
            >
              <Avatar>{initials}</Avatar>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'baseline',
                  }}
                >
                  <Typography variant="subtitle2" noWrap fontWeight="bold">
                    {author.full_name}
                  </Typography>
                  <Typography variant="caption" color="secondary" noWrap>
                    {formattedDate}
                  </Typography>
                  {currentUser?.is_superuser ? (
                    <Typography variant="caption" color="secondary" noWrap>
                      {t('annotations.commentBubble.adminMeta', {
                        values: {
                          aiVersion: comment.safi_ai_version ?? '-',
                          commentId: comment.id,
                        },
                      })}
                    </Typography>
                  ) : null}
                </Stack>
                <Typography
                  variant="body2"
                  data-testid="comment-bubble-text"
                  sx={{
                    mt: 0.5,
                  }}
                >
                  {formattedComment}
                </Typography>
              </Box>

              {author.id === currentUser?.id && (
                <Menu
                  onClick={(e) => e.stopPropagation()}
                  button={
                    <IconButton size="small" onClick={(e) => e.stopPropagation()}>
                      <MoreHorizOutlined data-testid="MoreHorizOutlinedIcon" />
                    </IconButton>
                  }
                >
                  <MenuItem onClick={onEdit}>
                    <ListItemIcon>
                      <Edit fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('annotations.commentBubble.actions.edit')}</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleDeleteClick}>
                    <ListItemIcon>
                      <DeleteOutlined
                        fontSize="small"
                        sx={({ palette }) => ({
                          color: palette.error[200],
                        })}
                      />
                    </ListItemIcon>
                    <ListItemText
                      sx={({ palette }) => ({
                        color: palette.error[200],
                      })}
                    >
                      {t('annotations.commentBubble.actions.deleteFeedback')}
                    </ListItemText>
                  </MenuItem>
                </Menu>
              )}
            </Stack>
          </Paper>

          <ConfirmationDialog
            open={isDeleteDialogOpen}
            handleCancel={handleCancelDelete}
            handleConfirm={handleConfirmDelete}
            title={t('annotations.commentBubble.deleteDialog.title')}
            message={t('annotations.commentBubble.deleteDialog.message')}
            slotProps={{
              confirmButton: {
                children: t('annotations.commentBubble.deleteDialog.confirm'),
                variant: 'primary',
              },
            }}
          />
        </div>
      </ClickAwayListener>
    </Popper>
  );
};
